import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchRecordsByIds } from '../services/core/queries';

const ACCESS_DENIED_CODES = new Set(['permission-denied', 'access-denied']);

/**
 * useLinkedRecordPreview
 *
 * Hydrates linked-record metadata (title/body snippet/status) on demand.
 * Shared by list/detail card and record editor link lists.
 */
export default function useLinkedRecordPreview(link) {
  const { t } = useTranslation();
  const isRecordLink = link.targetType === 'record';
  const [resolvedRecord, setResolvedRecord] = useState(null);
  const [status, setStatus] = useState(isRecordLink ? 'idle' : 'ready');

  useEffect(() => {
    if (!isRecordLink) {
      setResolvedRecord(null);
      setStatus('ready');
      return;
    }

    let cancelled = false;
    setResolvedRecord(null);
    setStatus('loading');

    fetchRecordsByIds([link.targetId])
      .then((records) => {
        if (cancelled) return;
        const match = records.find((item) => item?.id === link.targetId);
        if (match) {
          setResolvedRecord(match);
          setStatus('ready');
        } else {
          setResolvedRecord(null);
          setStatus('denied');
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load linked record', error);
        const code = error?.code || error?.cause?.code || null;
        const message = code && ACCESS_DENIED_CODES.has(code) ? 'denied' : 'error';
        setResolvedRecord(null);
        setStatus(message);
      });

    return () => {
      cancelled = true;
    };
  }, [isRecordLink, link.targetId]);

  const fallbackTitle = useMemo(() => {
    if (link.targetType !== 'external' || typeof link.targetId !== 'string') {
      return t('records.detail.linked_records.open_record', 'Open linked record');
    }
    try {
      const url = new URL(link.targetId);
      return url.host || link.targetId;
    } catch (err) {
      return link.targetId;
    }
  }, [link.targetType, link.targetId, t]);

  const derivedTitle = useMemo(() => {
    if (resolvedRecord?.content?.title) {
      return resolvedRecord.content.title;
    }

    if (!isRecordLink) {
      return fallbackTitle;
    }

    switch (status) {
      case 'denied':
        return t('records.detail.linked_records.denied_title', 'No permission');
      case 'loading':
        return t('records.detail.linked_records.loading_title', 'Loading linked record...');
      case 'error':
        return t('records.detail.linked_records.error_title', 'Unable to load link');
      default:
        return t('records.detail.linked_records.open_record', 'Open linked record');
    }
  }, [resolvedRecord, isRecordLink, fallbackTitle, status, t]);

  const bodySnippet = useMemo(() => {
    if (!resolvedRecord?.content?.body) return null;
    return resolvedRecord.content.body.substring(0, 80);
  }, [resolvedRecord]);

  const derivedExcerpt = useMemo(() => {
    if (bodySnippet) {
      return bodySnippet;
    }

    if (!isRecordLink) {
      return t('records.detail.linked_records.open_external', 'Open external link');
    }

    switch (status) {
      case 'denied':
        return '';
      case 'loading':
        return t('records.detail.linked_records.loading_excerpt', 'Fetching link details...');
      case 'error':
        return t('records.detail.linked_records.error_excerpt', 'Unable to load link');
      default:
        return '';
    }
  }, [bodySnippet, isRecordLink, status, t]);

  return {
    status,
    fallbackTitle,
    title: derivedTitle,
    excerpt: derivedExcerpt,
    record: resolvedRecord,
  };
}
